import { Link } from 'react-router-dom';
import { ArrowRight, Search, Store, Zap, BarChart3 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';

export default function HowItWorks() {
  const { language } = useLanguage();

  const steps = [
    {
      number: '01',
      icon: Search,
      title: language === 'pt' ? 'Diagnóstico cirúrgico' : 'Surgical diagnosis',
      description:
        language === 'pt'
          ? 'Entendemos exatamente onde a sua operação trava hoje. Avaliamos sua loja, suas conversões e seus processos para encontrar o ponto de maior alavancagem.'
          : 'We understand exactly where your operation bottlenecks today. We assess your store, your conversions, and your processes to find the highest leverage point.',
      color: 'text-cyan-400',
      bg: 'bg-cyan-500/10',
      border: 'border-cyan-500/50',
    },
    {
      number: '02',
      icon: Store,
      title: language === 'pt' ? 'Ataque ao maior gargalo' : 'Attack the biggest bottleneck',
      description:
        language === 'pt'
          ? 'Não tentamos consertar tudo de uma vez. Implementamos primeiro a solução que vai trazer o impacto financeiro mais rápido para o seu caixa.'
          : 'We do not try to fix everything at once. We first implement the solution that will bring the fastest financial impact to your cash flow.',
      color: 'text-purple-400',
      bg: 'bg-purple-500/10',
      border: 'border-purple-500/50',
    },
    {
      number: '03',
      icon: Zap,
      title: language === 'pt' ? 'Escala no piloto automático' : 'Scale on autopilot',
      description:
        language === 'pt'
          ? 'Com o problema principal resolvido, conectamos as ferramentas e removemos as tarefas manuais. A sua operação ganha velocidade sem virar um caos.'
          : 'With the main problem solved, we connect the tools and remove manual tasks. Your operation gains speed without turning into chaos.',
      color: 'text-emerald-400',
      bg: 'bg-emerald-500/10',
      border: 'border-emerald-500/50',
    },
    {
      number: '04',
      icon: BarChart3,
      title: language === 'pt' ? 'Lucro e próximo nível' : 'Profit and next level',
      description:
        language === 'pt'
          ? 'Medimos o resultado real em dashboards claros. Com o caixa respirando e a operação fluindo, decidimos juntos qual é a sua próxima etapa de crescimento.'
          : 'We measure real results in clear dashboards. With a breathing cash flow and a smooth operation, we decide together what your next growth stage is.',
      color: 'text-amber-400',
      bg: 'bg-amber-500/10',
      border: 'border-amber-500/50',
    },
  ];

  return (
    <div className="relative">
      <section className="relative py-20 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/50 to-slate-950" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-900/70 border border-slate-800 text-cyan-400 font-medium text-sm mb-6 shadow-sm">
            <Zap className="w-4 h-4" />
            {language === 'pt' ? 'A jornada com a ProcessSurge' : 'The journey with ProcessSurge'}
          </div>
          <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-100 mb-6 max-w-4xl mx-auto leading-tight tracking-tight">
            {language === 'pt' ? 'Sem reuniões infinitas. Foco direto no resultado.' : 'No endless meetings. Direct focus on results.'}
          </h1>
          <p className="text-slate-400 text-lg lg:text-xl max-w-3xl mx-auto leading-relaxed">
            {language === 'pt'
              ? 'Esqueça os processos engessados de agências tradicionais. Nosso método é focado em descobrir onde você perde dinheiro hoje e resolver o problema o mais rápido possível.'
              : 'Forget the rigid processes of traditional agencies. Our method is focused on finding out where you lose money today and fixing the problem as quickly as possible.'}
          </p>
        </div>
      </section>

      <section className="py-16 bg-slate-950 border-t border-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-8">
            {steps.map((step, index) => (
              <div key={index} className="relative flex gap-6 group">
                {index !== steps.length - 1 && (
                  <div className="absolute left-8 top-20 bottom-[-32px] w-0.5 bg-slate-800 group-hover:bg-slate-700 transition-colors" />
                )}

                <div className={`relative z-10 w-16 h-16 ${step.bg} border-2 ${step.border} rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg`}>
                  <step.icon className={`w-7 h-7 ${step.color}`} />
                </div>

                <div className="flex-1 bg-slate-900/50 border border-slate-800 rounded-2xl p-6 lg:p-8 hover:border-slate-700 transition-colors">
                  <div className="flex items-center gap-3 mb-2">
                    <span className={`text-sm font-bold ${step.color}`}>{step.number}</span>
                    <h3 className="font-outfit text-xl font-bold text-slate-100">{step.title}</h3>
                  </div>
                  <p className="text-slate-400 leading-relaxed">{step.description}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-16 text-center">
            <Link to={`/${language}/contact`}>
              <Button className="bg-cyan-500 text-slate-950 hover:bg-cyan-400 font-bold rounded-full px-10 py-6 text-lg shadow-lg shadow-cyan-500/20">
                {language === 'pt' ? 'Quero diagnosticar minha operação' : 'I want to diagnose my operation'}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
